import { useCallback, useState } from "react";
import {
  detectPendingToolCalls as sharedDetectPendingToolCalls,
  type PendingToolCall,
} from "@promptlayer/ui";

import { ChatMessagePayload } from "../ChatInput/hooks/useChatMessageInput";

import { FALLBACK_PLAYGROUND_PROVIDER_SLUG } from "@/components/Playground/ProviderApiKeyPrompt/constants";
import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { usePlayground } from "@/components/Playground/playground-store";
import { ChatMessageI, MessageType, PLAYGROUND_SESSION_MODES } from "@/components/Playground/types";
import { useAuth } from "@/context/auth-context";
import {
  useAPISendPlaygroundSessionChatMessages,
  SendPlaygroundSessionChatMessageResponseI,
} from "@/queries/playground/useAPISendPlaygroundSessionChatMessages";
import { useAPIDeletePlaygroundSessionChatMessages } from "@/queries/playground/useAPIDeletePlaygroundSessionChatMessages";
import { useAPIEditPlaygroundSessionChatMessage } from "@/queries/playground/useAPIEditPlaygroundSessionChatMessage";
import { useAPIDeletePlaygroundSessionChatMessage } from "@/queries/playground/useAPIDeletePlaygroundSessionChatMessage";
import {
  useAPIReplayPlaygroundSessionChatMessage,
  ReplayPlaygroundSessionChatMessageResponseI,
} from "@/queries/playground/useAPIReplayPlaygroundSessionChatMessage";
import { displayErrorToast } from "@/utils/toast";
import {
  DashboardProviderApiKeyErrorCode,
  isDashboardApiKeyHttpError,
} from "@/types/dashboard-api-key-api-error";
import { getMessageId, getToolReplayGroup } from "@/components/Playground/utils";
import { Content, ROLES, RoleI } from "@/types";

export type { PendingToolCall };

/**
 * Inspect the current chat messages and, when the very last message is an
 * assistant message that contains `tool_calls`, return ALL tool calls.
 * Each entry includes the `tool_call_id`, function `name`, and `arguments`.
 *
 * Returns an empty array when there are no pending tool calls.
 */
function detectPendingToolCalls(messages: ChatMessageI[]): PendingToolCall[] {
  return sharedDetectPendingToolCalls(
    messages.map(m => ({
      role: m.role as "user" | "assistant" | "tool",
      tool_calls: m.tool_calls,
    })),
  );
}

/**
 * Check if the last assistant message contains an `mcp_approval_request`
 * content block that hasn't been responded to yet.
 */
function detectPendingApproval(messages: ChatMessageI[]): {
  approval_request_id: string;
  name: string;
  server_label: string;
} | null {
  if (messages.length === 0) {
    return null;
  }

  const lastMessage = messages[messages.length - 1];
  if (lastMessage.role !== ROLES.ASSISTANT || !Array.isArray(lastMessage.content)) {
    return null;
  }

  const approvalBlock = lastMessage.content.find(c => c.type === "mcp_approval_request");
  if (!approvalBlock || approvalBlock.type !== "mcp_approval_request") {
    return null;
  }

  return {
    approval_request_id: approvalBlock.id ?? "",
    name: approvalBlock.name,
    server_label: approvalBlock.server_label,
  };
}

/** Mocked-agent-runner fields attached to every chat send while the mode is on. */
const mockRunFields = (playground: {
  mockToolResponses: boolean;
  mockScenario: string;
  mockStopTools: string;
}) =>
  playground.mockToolResponses
    ? {
        mock_tool_responses: true,
        mock_scenario: playground.mockScenario.trim() || null,
        mock_stop_tools: playground.mockStopTools.trim() || null,
      }
    : {};

const promptProvenanceFields = (playground: {
  selectedTemplateVersion?: { prompt_id: number; number: number };
}) => ({
  prompt_id: playground.selectedTemplateVersion?.prompt_id ?? null,
  prompt_version_number: playground.selectedTemplateVersion?.number ?? null,
});

const getUnknownErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return fallback;
};

export const useChatState = () => {
  const { variableSetIndex } = useChatWidgetContext();
  const playground = usePlayground();
  const { chatStore, workspace_id, instanceId } = playground;
  const { userToken } = useAuth() ?? {};
  const [isWaitingForSessionSave, setIsWaitingForSessionSave] = useState(false);

  const { mutate: sendMessageMutation, isPending: isMessageSending } =
    useAPISendPlaygroundSessionChatMessages(userToken ?? "", {
      onSuccess: (response: SendPlaygroundSessionChatMessageResponseI) => {
        playground.clearChatProviderApiKeyGate();
        // user_message may be a single dict or a list of dicts (multiple tool responses)
        const userMessages = Array.isArray(response.user_message)
          ? response.user_message
          : [response.user_message];

        userMessages.forEach((msg, idx) => {
          const sentMessage: ChatMessageI = {
            id: getMessageId(response.individual_run_request_id, msg.role as RoleI, idx),
            role: msg.role,
            content: msg.content,
            individual_run_request_id: response.individual_run_request_id,
            created_at: new Date().toISOString(),
            // Propagate tool-response fields when present
            ...(msg.tool_call_id ? { tool_call_id: msg.tool_call_id } : {}),
            ...(msg.name ? { name: msg.name } : {}),
          };

          chatStore.addMessage(variableSetIndex, sentMessage);
          chatStore.incrementTotalMessages(variableSetIndex);
        });

        chatStore.setPendingRequestId(variableSetIndex, response.individual_run_request_id);
      },
      onError: (error: unknown, variables) => {
        if (
          isDashboardApiKeyHttpError(error) &&
          error.error_code === DashboardProviderApiKeyErrorCode.PROVIDER_KEY_REQUIRED
        ) {
          playground.setChatProviderApiKeyGate(
            error.provider ?? FALLBACK_PLAYGROUND_PROVIDER_SLUG,
            {
              kind: "send",
              params: variables,
            },
          );
          displayErrorToast(error.message);
          chatStore.setPendingRequestId(variableSetIndex, null);
          return;
        }
        displayErrorToast(getUnknownErrorMessage(error, "Failed to send message"));
        chatStore.setPendingRequestId(variableSetIndex, null);
      },
    });

  const deleteMutation = useAPIDeletePlaygroundSessionChatMessages(userToken ?? "", {
    onSuccess: () => {
      chatStore.clearMessages(variableSetIndex);
    },
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to clear chat history");
    },
  });

  // --- Edit individual message (optimistic) ---
  const editMessageMutation = useAPIEditPlaygroundSessionChatMessage(userToken ?? "", {
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to edit message");
    },
  });

  // --- Delete individual message (optimistic) ---
  const deleteMessageMutation = useAPIDeletePlaygroundSessionChatMessage(userToken ?? "", {
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to delete message");
    },
  });

  // --- Replay message ---
  const replayMessageMutation = useAPIReplayPlaygroundSessionChatMessage(userToken ?? "");

  const runAfterSessionSave = useCallback(
    async (request: () => void) => {
      setIsWaitingForSessionSave(true);
      try {
        await playground.waitForSessionSaveCompletion();
        request();
      } finally {
        setIsWaitingForSessionSave(false);
      }
    },
    [playground],
  );

  const sendMessage = useCallback(
    (payload: ChatMessagePayload) => {
      if (
        chatStore.isVariableSetPending(variableSetIndex) ||
        isMessageSending ||
        isWaitingForSessionSave
      ) {
        return;
      }

      // Ensure chat mode is persisted before sending so the backend reads the
      // latest session shape for this request.
      if (playground.sessionMode !== PLAYGROUND_SESSION_MODES.CHAT) {
        playground.setSessionMode(PLAYGROUND_SESSION_MODES.CHAT);
      }

      // Filter out empty text content and empty media variable content
      const filteredContent = payload.content.filter(c => {
        if (c.type === "text") {
          return c.text.trim() !== "";
        }
        if (c.type === "media_variable") {
          return c.name && c.name.trim() !== "";
        }

        return true;
      });

      if (filteredContent.length === 0) {
        return;
      }

      // --- Magic tool-call detection ---
      // When the last message in the chat is an assistant message containing
      // tool_calls, we automatically convert the outgoing message into a
      // tool-response message (role: "tool") linked to the first pending
      // tool call.  This lets the user simply type a response in the input
      // box without having to manually construct a tool response payload.
      const pendingToolCalls = detectPendingToolCalls(chatStore.getMessages(variableSetIndex));

      if (pendingToolCalls.length > 0) {
        // Build a tool-response message for EVERY pending tool call.
        // The user's typed content is used as the response for each call.
        const toolMessages = pendingToolCalls.map(tc => ({
          role: ROLES.TOOL as MessageType,
          content: filteredContent,
          tool_call_id: tc.tool_call_id,
          name: tc.name,
        }));

        // Send as a list so the backend appends all tool responses together
        // before invoking the LLM.
        void runAfterSessionSave(() => {
          sendMessageMutation({
            workspaceId: workspace_id,
            sessionId: instanceId,
            request: {
              variable_set_index: variableSetIndex,
              message: toolMessages.length === 1 ? toolMessages[0] : toolMessages,
              stream: true,
              ...mockRunFields(playground),
              ...promptProvenanceFields(playground),
            },
          });
        });
      } else {
        const message = {
          role: payload.role,
          content: filteredContent,
        };

        void runAfterSessionSave(() => {
          sendMessageMutation({
            workspaceId: workspace_id,
            sessionId: instanceId,
            request: {
              variable_set_index: variableSetIndex,
              message,
              stream: true,
              ...mockRunFields(playground),
              ...promptProvenanceFields(playground),
            },
          });
        });
      }
    },
    [
      workspace_id,
      instanceId,
      chatStore,
      sendMessageMutation,
      playground,
      variableSetIndex,
      isMessageSending,
      isWaitingForSessionSave,
      runAfterSessionSave,
    ],
  );

  /**
   * Send pre-built tool response messages directly.
   *
   * Unlike `sendMessage` (which auto-detects pending tool calls and uses the
   * same user-typed content for every call), this function accepts an array of
   * fully-formed tool-response message objects — one per tool call — so each
   * response can have different content.
   */
  const sendToolResponses = useCallback(
    (
      toolMessages: {
        role: MessageType;
        content: Content[];
        tool_call_id: string;
        name: string;
      }[],
    ) => {
      if (
        chatStore.isVariableSetPending(variableSetIndex) ||
        isMessageSending ||
        isWaitingForSessionSave
      ) {
        return;
      }

      if (playground.sessionMode !== PLAYGROUND_SESSION_MODES.CHAT) {
        playground.setSessionMode(PLAYGROUND_SESSION_MODES.CHAT);
      }

      void runAfterSessionSave(() => {
        sendMessageMutation({
          workspaceId: workspace_id,
          sessionId: instanceId,
          request: {
            variable_set_index: variableSetIndex,
            message: toolMessages.length === 1 ? toolMessages[0] : toolMessages,
            stream: true,
            ...mockRunFields(playground),
            ...promptProvenanceFields(playground),
          },
        });
      });
    },
    [
      workspace_id,
      instanceId,
      chatStore,
      sendMessageMutation,
      playground,
      variableSetIndex,
      isMessageSending,
      isWaitingForSessionSave,
      runAfterSessionSave,
    ],
  );

  const clearChat = useCallback(() => {
    deleteMutation.mutate({
      workspaceId: workspace_id,
      sessionId: instanceId,
      variableSetIndex,
    });
  }, [workspace_id, instanceId, deleteMutation, variableSetIndex]);

  /**
   * Edit an individual message's content with optimistic update.
   * Snapshots the previous content so we can roll back on failure.
   */
  const editMessage = useCallback(
    (messageId: string, newContent: Content[]) => {
      // Snapshot previous state for rollback
      const previousMessage = chatStore.getMessageById(messageId);
      if (!previousMessage) {
        return;
      }

      const previousContent = previousMessage.content;

      // Optimistic update
      chatStore.updateMessage(messageId, { content: newContent });

      editMessageMutation.mutate(
        {
          workspaceId: workspace_id,
          sessionId: instanceId,
          messageId,
          request: { content: newContent },
        },
        {
          onError: () => {
            // Rollback on failure
            chatStore.updateMessage(messageId, { content: previousContent });
          },
        },
      );
    },
    [workspace_id, instanceId, chatStore, editMessageMutation],
  );

  /**
   * Delete an individual message with optimistic update.
   *
   * For messages that belong to an `IndividualRunRequest` turn the backend
   * deletes the entire record (user message + all response messages).
   * The optimistic update mirrors this by removing every message that
   * shares the same `individual_run_request_id`.
   *
   * For initial-history messages (`individual_run_request_id` is falsy)
   * only the single targeted message is removed.
   */
  const deleteMessage = useCallback(
    (messageId: string) => {
      // Snapshot previous state for rollback
      const previousMessages = [...chatStore.getMessages(variableSetIndex)];
      const previousTotal = chatStore.getStats(variableSetIndex).total_messages;

      // Find the message to determine if it belongs to a turn
      const targetMessage = previousMessages.find(m => m.id === messageId);
      if (!targetMessage) {
        return;
      }

      // Optimistic update
      if (targetMessage.individual_run_request_id) {
        // Turn message — remove all messages from the same turn
        const removedCount = chatStore.removeMessagesByRequestId(
          variableSetIndex,
          targetMessage.individual_run_request_id,
        );
        chatStore.decrementTotalMessages(variableSetIndex, removedCount);
      } else {
        // Initial-history message — remove just this one
        chatStore.removeMessage(variableSetIndex, messageId);
        chatStore.decrementTotalMessages(variableSetIndex, 1);
      }

      deleteMessageMutation.mutate(
        {
          workspaceId: workspace_id,
          sessionId: instanceId,
          messageId,
          variableSetIndex,
        },
        {
          onError: () => {
            // Rollback on failure — restore full snapshot
            chatStore.setMessages(variableSetIndex, previousMessages);
            chatStore.setStats(variableSetIndex, {
              ...chatStore.getStats(variableSetIndex),
              total_messages: previousTotal,
            });
          },
        },
      );
    },
    [workspace_id, instanceId, chatStore, deleteMessageMutation, variableSetIndex],
  );

  /**
   * Replay from a user or tool-response message.
   *
   * Calls a single backend endpoint that atomically:
   * 1. Truncates the conversation from the target message onward
   * 2. Re-sends the same message to get a fresh response
   *
   * The frontend optimistically removes messages from the target onward,
   * then lets the backend response + WebSocket handle the new turn.
   */
  const replayMessage = useCallback(
    (messageId: string) => {
      if (chatStore.isVariableSetPending(variableSetIndex)) {
        return;
      }

      const messages = chatStore.getMessages(variableSetIndex);
      const targetIndex = messages.findIndex(m => m.id === messageId);
      if (targetIndex === -1) {
        return;
      }

      const targetMessage = messages[targetIndex];
      // Allow replaying both user messages and tool-response messages
      if (targetMessage.role !== ROLES.USER && targetMessage.role !== ROLES.TOOL) {
        return;
      }

      // Snapshot for rollback
      const previousMessages = [...messages];
      const previousTotal = chatStore.getStats(variableSetIndex).total_messages;

      // Parallel tool responses are submitted as one turn. Replaying any sibling
      // must re-send the full group so earlier tool results are not dropped.
      const replayGroup =
        targetMessage.role === ROLES.TOOL
          ? getToolReplayGroup(messages, targetIndex)
          : { startIndex: targetIndex, messages: [targetMessage] };
      const truncateFromIndex = replayGroup.startIndex;

      // Optimistic update: remove all messages from the first replayed sibling onward
      const messagesToRemove = messages.slice(truncateFromIndex);
      chatStore.setMessages(variableSetIndex, messages.slice(0, truncateFromIndex));
      chatStore.decrementTotalMessages(variableSetIndex, messagesToRemove.length);

      // Build the message payload from the local store (source of truth).
      // This ensures that if the user edited the message, the replay uses
      // the edited content even if the preceding PATCH hasn't committed yet.
      const messagePayloads = replayGroup.messages.map(message => ({
        role: message.role as MessageType,
        content: message.content,
        ...(message.tool_call_id ? { tool_call_id: message.tool_call_id } : {}),
        ...(message.name ? { name: message.name } : {}),
      }));
      const messagePayload = messagePayloads.length === 1 ? messagePayloads[0] : messagePayloads;

      const replayParams = {
        workspaceId: workspace_id,
        sessionId: instanceId,
        messageId: messages[truncateFromIndex].id,
        request: {
          variable_set_index: variableSetIndex,
          stream: true,
          message: messagePayload,
          ...mockRunFields(playground),
        },
      };

      replayMessageMutation.mutate(replayParams, {
        onSuccess: (response: ReplayPlaygroundSessionChatMessageResponseI) => {
          playground.clearChatProviderApiKeyGate();
          // user_message may be a single dict or a list of dicts (multiple tool responses)
          const userMessages = Array.isArray(response.user_message)
            ? response.user_message
            : [response.user_message];

          userMessages.forEach((msg, idx) => {
            const sentMessage: ChatMessageI = {
              id: getMessageId(response.individual_run_request_id, msg.role as RoleI, idx),
              role: msg.role,
              content: msg.content,
              individual_run_request_id: response.individual_run_request_id,
              created_at: new Date().toISOString(),
              ...(msg.tool_call_id ? { tool_call_id: msg.tool_call_id } : {}),
              ...(msg.name ? { name: msg.name } : {}),
            };

            chatStore.addMessage(variableSetIndex, sentMessage);
            chatStore.incrementTotalMessages(variableSetIndex);
          });

          chatStore.setPendingRequestId(variableSetIndex, response.individual_run_request_id);
        },
        onError: (error: unknown) => {
          if (
            isDashboardApiKeyHttpError(error) &&
            error.error_code === DashboardProviderApiKeyErrorCode.PROVIDER_KEY_REQUIRED
          ) {
            playground.setChatProviderApiKeyGate(
              error.provider ?? FALLBACK_PLAYGROUND_PROVIDER_SLUG,
              {
                kind: "replay",
                params: replayParams,
              },
            );
            displayErrorToast(error.message);
            chatStore.setMessages(variableSetIndex, previousMessages);
            chatStore.setStats(variableSetIndex, {
              ...chatStore.getStats(variableSetIndex),
              total_messages: previousTotal,
            });
            return;
          }
          // Rollback on failure — restore full snapshot
          chatStore.setMessages(variableSetIndex, previousMessages);
          chatStore.setStats(variableSetIndex, {
            ...chatStore.getStats(variableSetIndex),
            total_messages: previousTotal,
          });
          displayErrorToast(getUnknownErrorMessage(error, "Failed to replay message"));
        },
      });
    },
    [workspace_id, instanceId, chatStore, playground, replayMessageMutation, variableSetIndex],
  );

  const retryChatAfterProviderApiKeySaved = useCallback(() => {
    const pending = playground.pendingChatRetryAfterApiKey;
    playground.clearChatProviderApiKeyGate();
    if (!pending) {
      return;
    }
    switch (pending.kind) {
      case "send":
        sendMessageMutation(pending.params);
        break;
      case "replay":
        replayMessage(pending.params.messageId);
        break;
      default:
        break;
    }
  }, [playground, replayMessage, sendMessageMutation]);

  const pendingToolCalls = detectPendingToolCalls(chatStore.getMessages(variableSetIndex));
  const isToolResponseMode = pendingToolCalls.length > 0;

  const pendingApproval = detectPendingApproval(chatStore.getMessages(variableSetIndex));
  const isApprovalMode = pendingApproval !== null;

  const approveToolCall = useCallback(
    (approve: boolean) => {
      const approval = detectPendingApproval(chatStore.getMessages(variableSetIndex));
      if (
        !approval ||
        chatStore.isVariableSetPending(variableSetIndex) ||
        isMessageSending ||
        isWaitingForSessionSave
      ) {
        return;
      }

      if (playground.sessionMode !== PLAYGROUND_SESSION_MODES.CHAT) {
        playground.setSessionMode(PLAYGROUND_SESSION_MODES.CHAT);
      }

      const message = {
        role: ROLES.USER as MessageType,
        content: [
          {
            type: "mcp_approval_response" as const,
            approval_request_id: approval.approval_request_id,
            approve,
          },
        ],
      };

      void runAfterSessionSave(() => {
        sendMessageMutation({
          workspaceId: workspace_id,
          sessionId: instanceId,
          request: {
            variable_set_index: variableSetIndex,
            message,
            stream: true,
            ...mockRunFields(playground),
          },
        });
      });
    },
    [
      workspace_id,
      instanceId,
      chatStore,
      sendMessageMutation,
      playground,
      variableSetIndex,
      isMessageSending,
      isWaitingForSessionSave,
      runAfterSessionSave,
    ],
  );

  return {
    messages: chatStore.getMessages(variableSetIndex),
    stats: chatStore.getStats(variableSetIndex),
    isLoading:
      chatStore.isVariableSetPending(variableSetIndex) ||
      isMessageSending ||
      isWaitingForSessionSave,
    isStreaming: chatStore.getStreamingMessageId(variableSetIndex) !== null,
    isClearing: deleteMutation.isLoading,
    isToolResponseMode,
    pendingToolCalls,
    isApprovalMode,
    pendingApproval,

    sendMessage,
    sendToolResponses,
    clearChat,
    editMessage,
    deleteMessage,
    replayMessage,
    approveToolCall,
    retryChatAfterProviderApiKeySaved,
  };
};
